/* eslint-disable no-unused-vars */
import React, { useCallback } from "react"
import clsx from "clsx"
import { isEqual } from "lodash-es"
import { useDrop } from "react-dnd"
import { useDispatch, useSelector } from "react-redux"
import {
  facilityDepartmentMemberSegmentCounts,
  facilityId as getFacilityId,
  addMemberToDepartmentSegment,
  removeMemberFromDepartmentSegment,
} from "reduxSlices/adminFacilitySlice"
import { useCustomName, useOncePerPageLoadMessage, useSCIMResourceManagement } from "hooks"
import * as API from "services/api"
import { DEPARTMENT_KEY } from "utils/customNameHelpers"
import { adminFacilityDepartmentShape } from "utils/propTypeShapes"
import { TrashCanIcon } from "shared/icons"
import { errorToast } from "shared/toast"
import { DraggableItemTypes } from "../DraggableItemTypes"

const departmentRemovalMessage = (departmentNameInSentence) => (
  `Removing a user from a ${departmentNameInSentence} does not remove the user `
  + "from any existing tasks or form submissions. Removal from existing tasks "
  + "or form submissions must be done manually or by using "
  + "the Historical Access Tool under the user's profile in the Admin area."
)

const RemoveMemberDropzone = ({ department }) => {
  const { nameInSentence: departmentNameInSentence } = useCustomName(DEPARTMENT_KEY)

  const {
    displayMessage: displayRemovalMessage,
  } = useOncePerPageLoadMessage(departmentRemovalMessage(departmentNameInSentence), "department_removal_message")

  const { id: dropzoneDepartmentId } = department

  const dispatch = useDispatch()

  const { departmentsManagedBySCIM } = useSCIMResourceManagement()

  const facilityId = useSelector(getFacilityId)
  const memberSegmentCounts = useSelector(facilityDepartmentMemberSegmentCounts({ departmentId: dropzoneDepartmentId }), isEqual)

  const canRemoveMember = useCallback(({ departmentId, memberId }) => {
    if (departmentId !== dropzoneDepartmentId) return false
    if (!departmentsManagedBySCIM) return true

    return memberSegmentCounts[memberId] && memberSegmentCounts[memberId] >= 2
  }, [departmentsManagedBySCIM, dropzoneDepartmentId, memberSegmentCounts])

  const removeDepartmentMemberFromSegment = useCallback(async ({
    departmentId, memberId, user, segmentName,
  }) => {
    dispatch(removeMemberFromDepartmentSegment({ departmentId, memberId, segmentName }))

    const response = await API.removeUserFromDepartmentSegment({
      facilityId, departmentId, memberId, segmentName,
    })

    if (response.ok) {
      displayRemovalMessage()
    } else {
      dispatch(addMemberToDepartmentSegment({ departmentId, member: user, segmentName }))
      errorToast(`Something went wrong.  Unable to remove member from ${departmentNameInSentence} segment.`)
    }
  }, [dispatch, displayRemovalMessage, facilityId, departmentNameInSentence])

  const [collected, drop] = useDrop(() => ({
    accept: DraggableItemTypes.MEMBER_PROFILE_ICON,
    canDrop: (item, monitor) => (
      canRemoveMember({ departmentId: item.departmentId, memberId: item.memberId })
    ),
    collect: (monitor, props) => ({
      canDrop: monitor.canDrop(),
      isOver: monitor.isOver(),
    }),
    drop: (item, monitor) => {
      removeDepartmentMemberFromSegment(item)
    },
  }), [canRemoveMember, removeDepartmentMemberFromSegment])

  const className = clsx(
    "rounded p-1 border",
    (
      collected.isOver && collected.canDrop
        ? "bg-green-200 border-green-800"
        : "bg-primary-light border-light"
    ),
  )

  return (
    <div ref={drop} className={className}>
      <TrashCanIcon className="w-4 h-4" />
    </div>
  )
}

RemoveMemberDropzone.propTypes = {
  department: adminFacilityDepartmentShape.isRequired,
}

export default RemoveMemberDropzone
